<template>
  <div class="thankyou-root">
    <thankyou-layout />
    <div class="contentWrapper">
      <div class="postLogin-container" :style="thankyouLayout">
        <div
          v-if="showInternetProbe"
          class="wifi-loader-area"
          style="text-align: center; width: 100%"
        >
          <transition v-if="!showConnectionError" name="slide-fade">
            <div v-if="!isOnline" class="loader">
              <wifi-loader />
            </div>
          </transition>
          <wifi-icon v-if="showConnectionError" />
          <p v-if="isDebugging" :key="`debuggerKey_${debuggerKey}`">
            {{ debugging }}
          </p>
          <p v-if="currentAttempt > 0">
            {{ connectionStatusMessage }}
          </p>
        </div>

        <!-- if thankyou has internt -->
        <div v-if="!showInternetProbe && isOnline && interstitialChecked" style="width: 100%">
          <ads
            v-if="hasTopAd && !hasPreviewAds && hasAdvertising && canLoadTopAd"
            class="ads-area"
            :campaign-id="campaignId"
            :zone-id="hasTopAd?.id"
            :position="'top'"
            type="post"
            @ad-loaded="onAdLoaded"
          />

          <ads-placeholder v-if="hasPreviewAds && hasTopAd" />

          <div v-if="thankYouElements">
            <component
              :is="element.action.replace(' ', '')"
              v-for="(element, index) in firstHalfElements"
              :key="`first-${element.action}-${index}`"
              :option="thankYouElements"
            />
            <ads
              v-if="hasMiddleAd && !hasPreviewAds && hasAdvertising && canLoadMiddleAd"
              class="ads-area"
              :campaign-id="campaignId"
              :zone-id="hasMiddleAd?.id"
              :position="'middle'"
              type="post"
              @ad-loaded="onAdLoaded"
            />
            <ads-placeholder v-if="hasPreviewAds && hasMiddleAd" />
            <component
              :is="element.action.replace(' ', '')"
              v-for="(element, index) in secondHalfElements"
              :key="`second-${element.action}-${index}`"
              :option="thankYouElements"
            />
          </div>

          <div v-else style="width: 100%">
            <div v-if="isOnline" class="thankyou-html-area" v-html="thankYouData" />

            <ads
              v-if="hasMiddleAd && !hasPreviewAds && hasAdvertising && canLoadMiddleAd"
              class="ads-area"
              :campaign-id="campaignId"
              :zone-id="hasMiddleAd?.id"
              :position="'middle'"
              type="post"
              @ad-loaded="onAdLoaded"
            />

            <facebook-like v-if="hasFacebookLike && facebookPage" />
          </div>

          <div v-if="hasRedirect" class="redirect-area" style="width: 100%">
            <p v-if="!isManual && isOnline" class="redirect-time">{{ timer }} s</p>
            <p v-if="isManual && redirectUrl">
              <a class="next-btn" :href="redirectUrl"> → </a>
            </p>
          </div>

          <ads
            v-if="hasBottomAd && !hasPreviewAds && hasAdvertising && canLoadBottomAd"
            class="ads-area"
            :campaign-id="campaignId"
            :zone-id="hasBottomAd?.id"
            :position="'bottom'"
            type="post"
            @ad-loaded="onAdLoaded"
          />

          <ads-placeholder v-if="hasPreviewAds && hasBottomAd" />

          <facebook-pixel v-if="facebookPixelId" />

          <div v-if="conversionTrackingCode">
            <div v-html="conversionHTML" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Ads from '../components/ads/ads'
  import FacebookLike from '../components/facebook-sdk/facebook-like'
  import FacebookPixel from '../components/facebook-sdk/facebook-pixel'
  import CustomText from '../components/thankyou-elements/custom-text'
  import AddImage from '../components/thankyou-elements/custom-image'
  import AddHTML from '../components/thankyou-elements/custom-html'
  import LikeBox from '../components/thankyou-elements/like-box'
  import AdsPlaceholder from './ads-placeholder'
  import screens from '../utils/pageSchema'
  import WifiLoader from '../components/loader/wifi-loading.vue'
  import axios from 'axios'
  import AlertMsg from '../components/errors/alert.vue'
  import WifiIcon from '../components/icons/wifi-icon.vue'
  import ThankyouLayout from './thankyouLayout.vue'
  import { defaultConnectionCheckConfig } from '../helpers'

  const $HOSTS = [
    // 'https://publishers.sambanetworks.com', // This one is for testing
    'https://api.ipify.org', // 3rd party from across the internet
    'https://b2gnrdcb4uq7mj322t3lxzmea40wzlye.lambda-url.us-east-1.on.aws', // controlled by MyWiFi Lambda
    'https://httpbin.org/status/200', // 3rd party suggested by plume
  ]
  const $TIMEOUT = 5000
  const $DELAY = 3500

  export default {
    components: {
      WifiIcon,
      AlertMsg,
      WifiLoader,
      Ads,
      FacebookLike,
      FacebookPixel,
      AdsPlaceholder,
      CustomText,
      AddImage,
      AddHTML,
      LikeBox,
      ThankyouLayout,
    },
    data() {
      return {
        interstitialChecked: false, // Track if we've checked for interstitial
        timer: null,
        isOnline: false,
        intervalId: null,
        showConnectionError: false,
        currentAttempt: 0,
        debugging: null,
        isDebugging: false,
        currentHost: null,
        debuggerKey: 0,
        showInternetProbe: true,
        timerStarted: false,
        // Sequential ad loading tracking
        loadedAds: new Set(),
        adLoadingOrder: ['top', 'middle', 'bottom'],
        // Reactive trigger for ad loading
        adLoadingTrigger: 0,
      }
    },

    computed: {
      connectionStatusMessage() {
        if (!this.isOnline && this.currentAttempt <= this.maxRetryNumber) {
          return this.$t('attempt_x_of_y', {
            x: this.currentAttempt,
            y: this.maxRetryNumber,
          })
        }

        if (!this.isOnline) {
          return this.$t('internet_connection_error')
        }

        return ''
      },
      thankyouLayout() {
        return {
          'min-height': '100%',
          display: 'flex',
          'align-items': this.baseData.fonts.justifyContent.thankYouPage || 'flex-start',
          color: this.baseData.colors.textColor,
          'font-family': this.baseData.fonts.font,
          'min-width': '100%',
        }
      },
      hasPreviewAds() {
        return !!(
          this.baseData?.flags?.isAdvertising &&
          this.baseData?.flags?.adsThankYou &&
          this.baseData?.development?.preview
        )
      },
      baseData() {
        return this.$store.state.baseData
      },
      connectionCheckConfig() {
        // Merge with defaults so partial campaign config doesn't lose enabled=true
        return {
          ...defaultConnectionCheckConfig,
          ...(this.baseData?.connectionCheck || {}),
        }
      },
      maxRetryNumber() {
        return this.connectionCheckConfig.maxAttempts
      },
      connectionCheckEnabled() {
        return this.connectionCheckConfig.enabled
      },
      postLogin() {
        return this.$store.getters.postLogin
      },
      campaignId() {
        return this.$store.getters.postLogin?.id || null
      },
      thankYouData() {
        return this.$t('enjoy_browsing')
      },
      thankYouPage() {
        return this.baseData?.postLogin?.thankYouPage ?? {}
      },
      order() {
        return this.thankYouPage.order
          ? this.thankYouPage.order
          : ['Like Box', 'Custom Text', 'Add Image', 'Add HTML']
      },
      thankYouElements() {
        return this.thankYouPage.options
      },
      userOS() {
        return this.postLogin?.user_os ?? ''
      },
      redirectOption() {
        return this.baseData?.postLogin?.redirect?.options || []
      },
      redirectUrl() {
        const opt = this.redirectOption
        if (!opt) return ''
        switch (opt.action) {
          case 'URL':
            return this.postLogin?.next_url ?? opt.url
          case 'App Store':
            if (['android', 'windows'].includes(this.userOS)) {
              return opt.androidUrl || ''
            }
            if (['os x', 'ios'].includes(this.userOS)) {
              return opt.appleUrl || ''
            }
            return ''
          case 'WhatsApp': {
            const phone =
              opt.phone && typeof opt.phone === 'object' && opt.phone.stripped
                ? opt.phone.stripped
                : typeof opt.phone === 'string'
                ? opt.phone
                : ''
            if (phone) {
              return `https://wa.me/${encodeURIComponent(phone)}?text=${encodeURIComponent(
                opt.message || '',
              )}`
            }
            return ''
          }
          case 'Messenger':
            if (opt.fbPage) {
              let url = `https://m.me/${opt.fbPage}`
              if (opt.ref) url += `?ref=${encodeURIComponent(opt.ref)}`
              return url
            }
            return ''
          default:
            return ''
        }
      },

      redirectTime() {
        return this.baseData?.postLogin?.redirect?.redirectTime ?? 5
      },

      hasAdvertising() {
        return (this.baseData?.flags?.isAdvertising && this.baseData?.flags?.adsThankYou) ?? false
      },
      advertisementData() {
        return this.baseData?.advertisement?.postLogin ?? {}
      },
      hasTopAd() {
        return this.advertisementData?.top ?? false
      },
      hasMiddleAd() {
        return this.advertisementData?.middle ?? false
      },
      hasBottomAd() {
        return this.advertisementData?.bottom ?? false
      },
      facebookPage() {
        return this.postLogin?.facebook_page ?? null
      },
      hasFacebookLike() {
        return this.postLogin?.facebook_like ?? false
      },
      facebookPixelId() {
        return this.postLogin?.campaign_facebook_pixel_id ?? null
      },
      conversionTrackingCode() {
        return this.postLogin?.conversion_tracking_code ?? null
      },
      conversionHTML() {
        return this.conversionTrackingCode ? atob(this.conversionTrackingCode) : null
      },
      hasRedirect() {
        return this.baseData?.flags?.isRedirect
      },
      isManual() {
        return !!(this.redirectUrl && this.redirectTime === 0 && this.baseData?.flags?.isRedirect)
      },
      orderedThankYouElements() {
        if (!this.thankYouElements || !this.order) return []

        const ordered = []
        for (const actionName of this.order) {
          const element = this.thankYouElements.find((el) => el.action === actionName)
          if (element) {
            ordered.push(element)
          }
        }

        for (const element of this.thankYouElements) {
          if (!this.order.includes(element.action)) {
            ordered.push(element)
          }
        }

        return ordered
      },
      firstHalfElements() {
        return this.orderedThankYouElements.slice(
          0,
          Math.ceil(this.orderedThankYouElements.length / 2),
        )
      },
      secondHalfElements() {
        return this.orderedThankYouElements.slice(
          Math.ceil(this.orderedThankYouElements.length / 2),
          this.orderedThankYouElements.length,
        )
      },
      hasInterstitial() {
        const flags = this.baseData?.flags || {}
        const interstitialEnabled = flags.adsInterstitial
        return Boolean(
          flags.isAdvertising &&
            interstitialEnabled &&
            this.baseData?.advertisement?.interstitial?.top?.id,
        )
      },
      // Sequential ad loading controls
      canLoadTopAd() {
        return true // Top ad can always load first
      },
      canLoadMiddleAd() {
        this.adLoadingTrigger // Force reactivity
        return !this.hasTopAd || this.loadedAds.has('top')
      },
      canLoadBottomAd() {
        this.adLoadingTrigger // Force reactivity
        const topReady = !this.hasTopAd || this.loadedAds.has('top')
        const middleReady = !this.hasMiddleAd || this.loadedAds.has('middle')
        return topReady && middleReady
      },
    },

    watch: {
      async timer() {
        if (this.timer === 0 && !this.isManual) {
          if (this.redirectUrl && this.isOnline && this.baseData?.flags?.isRedirect) {
            let redirectUrl = this.redirectUrl
            const SUCID = localStorage.getItem('SUCID')

            const sucidEnabled = this.baseData?.flags?.isSucid || false
            if (sucidEnabled && SUCID) {
              const separator = redirectUrl.includes('?') ? '&' : '?'
              redirectUrl += `${separator}SUCID=${encodeURIComponent(SUCID)}`
            }
            window.location = redirectUrl
          }
        }
      },
    },
    async mounted() {
      if (this.isDebugging) {
        setInterval(() => this.debuggerKey++, 200)
      }

      const plume = localStorage.getItem('isPlume')
      const pathName = window.location.pathname
      const parts = pathName.split('/')
      const pageName = parts.filter((part) => part.includes('.html'))[0]
      const localStorageLanguage = JSON.parse(localStorage.getItem('selectedLanguage'))
      const value = localStorageLanguage[pageName]

      if (this.baseData.flags.showMultilingual || plume) {
        if (pageName && value) {
          this.$i18n.locale = value
        }
      } // single language campaign and non-plume campaign's it will use default language code
      if (this.baseData?.development?.preview) {
        this.showInternetProbe = false
        this.isOnline = true
        this.interstitialChecked = true
        return
      }

      const isChecked = sessionStorage.getItem('internetConnectChecked') === 'true'
      const isOnlineStorage = sessionStorage.getItem('isOnline') === 'true'

      // Check if this is a retry attempt
      const urlParams = new URLSearchParams(window.location.search)
      const retryParam = urlParams.get('retry')
      const isRetryAttempt = retryParam && parseInt(retryParam.split('_')[0]) > 1

      if (isChecked && isOnlineStorage) {
        this.isOnline = true
        this.showInternetProbe = false
        // After internet check, check for interstitial
        await this.checkAndNavigateToInterstitial()
        if (!this.interstitialChecked) return // If navigated to interstitial, don't continue
        await this.$nextTick()
        await this.handlePostInternetCheck()
        return
      }

      if (!this.connectionCheckEnabled) {
        this.isOnline = true
        this.showInternetProbe = false
        sessionStorage.setItem('internetConnectChecked', 'true')
        sessionStorage.setItem('isOnline', 'true')
        // After internet check, check for interstitial
        await this.checkAndNavigateToInterstitial()
        if (!this.interstitialChecked) return // If navigated to interstitial, don't continue
        await this.$nextTick()
        await this.handlePostInternetCheck()
        return
      }

      // Show loader while checking connection
      this.showInternetProbe = true
      this.isOnline = false

      try {
        await this.checkInternetConnection()
        sessionStorage.setItem('internetConnectChecked', 'true')
        sessionStorage.setItem('isOnline', 'true')
        this.isOnline = true
        this.showInternetProbe = false
        // After internet check passes, check for interstitial
        await this.checkAndNavigateToInterstitial()
        if (!this.interstitialChecked) return // If navigated to interstitial, don't continue
        await this.handlePostInternetCheck()
      } catch (error) {
        this.isOnline = false
        this.showInternetProbe = true
        // Don't check for interstitial if internet check failed
      }
    },

    beforeDestroy() {
      this.stopInterval()
    },

    methods: {
      async checkAndNavigateToInterstitial() {
        // Check for interstitial AFTER internet check passes
        // If interstitial exists and hasn't been shown, navigate immediately
        const interstitialShown = sessionStorage.getItem('interstitialShown') === 'true'
        if (this.hasInterstitial && !interstitialShown) {
          // Navigate to interstitial immediately, don't render anything on thankyou page
          await screens.advance()
          return // Don't set interstitialChecked, so page doesn't render
        }
        // Mark that we've checked for interstitial and it's safe to render
        this.interstitialChecked = true
      },
      async logToPortalWhenDebugging(message, ctx = undefined) {
        if (this.isSendingLogsOnDebugging) {
          const { landingPageLogUsingPortal } = await import('../main')
          await landingPageLogUsingPortal(message, ctx)
        }
      },
      getConversionCode() {
        return atob(this.conversionTrackingCode)
      },

      async checkInternetConnection() {
        const urlParams = new URLSearchParams(window.location.search)
        this.debugging = { urlParams: urlParams.toString() }

        if (urlParams.has('probe') && urlParams.get('probe') === 'done') {
          await this.logToPortalWhenDebugging(
            'Probe in query detected. Setting probe_detected and isOnline true',
          )
          this.debugging.probe_detected = true
          this.showInternetProbe = false
          this.isOnline = true
          return
        }

        await this.logToPortalWhenDebugging('Starting probes')
        this.debugging.probes = []

        if (!urlParams.has('retry')) {
          await this.logToPortalWhenDebugging('Has no retry, so this is a first attempt')
          this.debugging.set_first_retry = true
          urlParams.set('retry', `1_${Date.now()}`)
        }

        const attempt = parseInt(urlParams.get('retry').split('_')[0])
        this.currentAttempt = attempt

        try {
          let response = null
          let responseUrl = ''

          for (const host of $HOSTS) {
            try {
              this.currentHost = host

              await this.logToPortalWhenDebugging(
                `Started probe on host ${host} with delay ${$DELAY}`,
              )
              this.debugging.probes.push({
                target: host,
                status: 'started',
                delay: $DELAY,
              })
              await new Promise((resolve) => setTimeout(resolve, $DELAY))

              response = await axios.get(host, {
                timeout: $TIMEOUT,
                validateStatus: function (status) {
                  return status >= 200 && status < 300
                },
              })
              responseUrl = new URL(response.request.responseURL)
              const checkUrl = new URL(host)
              this.isOnline = response.status === 200 && responseUrl.host === checkUrl.host

              if (this.isOnline) {
                await this.logToPortalWhenDebugging(
                  `Detected online from ${host}. This is a good sign. Redirecting to the same page with the probe done flag`,
                )
                this.debugging.set_probe_done = true
                this.debugging.probes.push({ target: host, status: 'OK' })
                urlParams.set('probe', `done`)
                sessionStorage.setItem('internetConnectChecked', 'true')
                sessionStorage.setItem('isOnline', 'true')

                window.location.href =
                  window.location.protocol +
                  '//' +
                  window.location.host +
                  window.location.pathname +
                  '#/thankyou'
                this.showInternetProbe = false
                break
              } else {
                await this.logToPortalWhenDebugging(
                  `Host ${host} failed (expectation failed). Status: ${response.status}`,
                )
                this.debugging.probes.push({
                  target: host,
                  status: 'FAILED (expectation failed)',
                  responseCode: response.status,
                  responseHost: responseUrl.host,
                })
              }
            } catch (e) {
              await this.logToPortalWhenDebugging(
                `Host ${host} failed (in body). Error message: ${e.message}`,
              )
              this.debugging.probes.push({
                target: host,
                status: 'FAILED (in body)',
                errorMessage: e.message,
              })
            }
          }

          if (!this.isOnline) {
            await this.logToPortalWhenDebugging(
              `Not online. Throwing "Internet probe failed" error`,
            )
            this.debugging.round_failed = true
            throw new Error('Internet probe failed')
          }
        } catch (error) {
          await this.logToPortalWhenDebugging(
            `Caught error (in catch). Error message: ${error.message}`,
          )
          this.debugging.error_handler = true
          this.debugging.probes.push({
            target: this.currentHost,
            status: 'FAILED (in catch)',
            errorMessage: error.message,
          })
          if (this.isDebugging) {
            await new Promise((resolve) => setTimeout(resolve, 1000))
          }

          const setAttempt = `${attempt + 1}_${Date.now()}`
          await this.logToPortalWhenDebugging(`Setting retry ${setAttempt}`)
          urlParams.set('retry', setAttempt)

          if (this.currentAttempt > this.maxRetryNumber) {
            this.isOnline = true
            this.showInternetProbe = false
            this.showConnectionError = false
            sessionStorage.setItem('internetConnectChecked', 'true')
            sessionStorage.setItem('isOnline', 'true')
            urlParams.set('probe', 'done')
          }

          window.location.href =
            window.location.protocol +
            '//' +
            window.location.host +
            window.location.pathname +
            '?' +
            urlParams.toString()
        }
      },
      stopInterval() {
        if (this.intervalId) {
          clearInterval(this.intervalId)
        }
      },
      async handlePostInternetCheck() {
        // NOTE: Interstitial check is now handled by checkAndNavigateToInterstitial()
        // which is called BEFORE this method, so we don't need to check again here

        if (!this.isOnline) {
          this.showInternetProbe = true
          return
        }

        if (!this.hasAdvertising) {
          this.startTimer()
          this.timerStarted = true
          return
        }

        if (!this.hasTopAd && !this.hasMiddleAd && !this.hasBottomAd) {
          this.startTimer()
          this.timerStarted = true
        }
      },
      onAdLoaded(adData) {
        // Track which ad was loaded
        if (adData && adData.position) {
          this.loadedAds.add(adData.position)
          // Force Vue reactivity by incrementing the trigger
          this.adLoadingTrigger++
        }

        // Start timer if not already started
        if (!this.timerStarted) {
          this.timerStarted = true
          this.startTimer()
        }
      },
      startTimer() {
        this.timer = this.redirectTime
        if (this.timer > 0) {
          this.intervalId = window.setInterval(() => {
            if (this.timer !== 0) {
              this.$set(this, 'timer', this.timer - 1)
            } else {
              clearInterval(this.intervalId)
            }
          }, 1000)
        }
        this.showInternetProbe = false
      },
    },
  }
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Alegreya+Sans:wght@500&family=Archivo&family=B612&family=BioRhyme&family=Cairo&family=Cardo&family=Crimson+Text&family=Fjalla+One&family=Frank+Ruhl+Libre&family=IBM+Plex+Sans&family=Karla&family=Lora&family=Montserrat&family=Muli:wght@577&family=Playfair+Display&family=Roboto&family=Rubik&family=Source+Sans+Pro&family=Spectral&family=Titillium+Web&family=Varela&family=Vollkorn&family=Work+Sans&display=swap');
  .thankyou-root {
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden;
  }
  thankyou-layout {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100%;
    z-index: 0;
    pointer-events: none;
  }
  .contentWrapper {
    position: relative;
    z-index: 1;
    width: 100vw;
    height: 100%;
    padding: 20px 0;
    overflow: auto;
    box-sizing: border-box;
  }
  .postLogin-container {
    width: 100%;
    max-width: none;
    text-align: center;
    margin: 0;
    background: transparent;
    padding: 0;
  }
  .wifi-loader-area {
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .redirect-area {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .redirect-time {
    font-size: 12px;
    background: #f4f4f4;
    display: inline-block;
    padding: 10px 20px;
    border-radius: 32px;
    font-weight: 600;
    color: #808080;
    margin-right: 10px;
  }
  .next-btn {
    background: #55acee;
    padding: 10px 20px;
    border-radius: 32px;
    color: white;
    text-decoration: none;
    font-size: 12px;
    cursor: pointer;
  }
</style>
<style>
  .thankyou-html-area img {
    max-width: 100%;
  }
</style>
